import React from "react";

type SegmentedProps<T extends string> = {
    options: { key: T; label: string }[];
    value: T;
    onChange: (value: T) => void;
    /** `lg` is the 44px touch-target size (cab tab rows); `sm` suits inline pickers. */
    size?: "sm" | "lg";
    /** How the active segment announces itself: raised surface, or the full accent fill. */
    tone?: "neutral" | "accent";
    /** Stretch every segment to an equal share of the width — the console's section tabs. */
    full?: boolean;
    className?: string;
};

/** A boxed segment group — one always-active choice among a few, like a tab row or theme picker. */
export function Segmented<T extends string>({
    options,
    value,
    onChange,
    size = "lg",
    tone = "neutral",
    full = false,
    className = "",
}: SegmentedProps<T>): React.ReactElement {
    // `full` shares the width equally from md up. Below that, six section tabs squeezed into a
    // phone's width truncate to their first letters, so the row wraps instead: every label stays
    // whole and the group just gets a second line. Wrapping, not a horizontal scroll — the
    // console's rule is that nothing on a phone scrolls sideways.
    const container = full
        ? "flex flex-wrap gap-1 rounded-[14px] border border-[var(--line)] bg-[var(--surface-2)] p-1 md:grid md:grid-flow-col md:auto-cols-fr"
        : size === "lg"
          ? "flex gap-1 rounded-xl bg-[var(--surface-2)] p-1"
          : "flex gap-1 rounded-[10px] bg-[var(--surface-2)] p-[3px]";
    const item = full
        ? "min-h-11 flex-1 whitespace-nowrap rounded-[10px] px-3 text-[13.5px] font-semibold md:truncate"
        : size === "lg"
          ? "min-h-11 flex-1 rounded-lg text-[12.5px] font-semibold"
          : "min-h-9 rounded-lg px-3 text-[11.5px] font-semibold";
    const active = tone === "accent" ? "bg-[var(--accent)] text-[var(--accent-in)]" : "bg-[var(--surface)] text-[var(--text)]";

    return (
        <div className={`${container} ${className}`} role="tablist">
            {options.map((o) => (
                <button
                    key={o.key}
                    type="button"
                    role="tab"
                    aria-selected={value === o.key}
                    onClick={() => onChange(o.key)}
                    className={`${item} ${value === o.key ? active : "text-[var(--muted)] hover:text-[var(--text)]"}`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}
